import { useSession } from '@/auth/useSession';
import { CopyableLink } from '@/components/copyable-link';
import { EditableInfoCard, SubmitProps } from '@/components/editable-info-card';
import { FormCheckbox } from '@/components/forms/form-checkbox';
import { FormComboBox } from '@/components/forms/form-combo-box';
import { FormInputDate } from '@/components/forms/form-input-date';
import { FormInputField } from '@/components/forms/form-input-field';
import { FormMultiSelect } from '@/components/forms/form-multi-select';
import { FormSingleSelect } from '@/components/forms/form-single-select';
import { InfoCard } from '@/components/info-card';
import { InfoKeyValue } from '@/components/info-key-value';
import { InfoPage } from '@/components/info-page';
import { InfoPageSuspenseBoundary } from '@/components/info-page-suspense-boundary';
import { LinkToExternal } from '@/components/link-to-external';
import { CancelSessionSubstituteByAdmin } from '@/components/session/cancel-session-substitue-by-admin';
import { CreateSubstitutionRequestByAdmin } from '@/components/session/create-substitution-request-by-admin';
import { Tooltip } from '@/components/tooltip';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { FormLabel } from '@/components/ui/form';
import { useToast } from '@/components/ui/hooks/use-toast';
import {
  buildSessionRemarksV2Options,
  buildUserSelectOptions,
  comboBoxOptionsOf,
  emptySelectOption,
  multiSelectOptions,
  requiredSelectOption,
} from '@/components/ui/multi-select-utils';
import {
  coachesTypeaheadSearch,
  getSubstituteCoachConflictsWithSession,
  lessonTypeaheadSearch,
} from '@/lib/api-call-hooks';
import {
  EmptyArray,
  getAdminSession,
  getAdminSessions,
  getSessionFeedbacks,
  getSessionsForStudentGroup,
  getStudentGroup,
  updateSessionDetails,
  updateSessionPeople,
} from '@/lib/api-calls';
import { airServerErrorFrom, axiosErrorFrom } from '@/lib/error-handling';
import { nullIfString, stringIfNull } from '@/lib/string-utils';
import { AllTimeZones, timezoneZodSchema } from '@/lib/time-zones';
import { toInputTimeValue } from '@/routes/_layout/scheduling/schedules/-components/formatting';
import { SessionStudentsTable } from '@/routes/_layout/sessions/-components/session-students-table';
import { SessionTeacherDisplay } from '@/routes/_layout/sessions/-components/session-teacher-display';
import {
  formatInstant,
  FrequentTimeZones,
  getCoachSessionRemarkLabel,
  getLearningLossLabel,
  normalizeOriginalTeacherIssueReason,
  getSessionFormattedSchedule,
  getSessionSubstitutionRequestSummary,
  InstantCompare,
  isAirAdmin,
  SessionStatusToLabel,
} from '@airreading/common';
import {
  allPagesModifier,
  CoachSession,
  CoachSessionRemark,
  CoachSessionRemarkV2,
  formatCoachSessionRemarkV2Label,
  CoachStatus,
  LearningLossLabelMap,
  learningLossSelectableSchema,
  learningLossSelectableValues,
  MeetingStatus,
  MeetingStatusLabelMap,
  originalTeacherIssueSelectableSchema,
  SessionStatus,
  sessionStatusSchema,
  UpdateSessionOptions,
  VideoConferenceType,
  VideoConferenceTypeLabels,
  videoConferenceTypeSchema,
} from '@airreading/common/data';
import { useMutation, useQuery, useQueryClient, useSuspenseQueries, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { ChevronDown, Info } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useWatch, UseFormReturn } from 'react-hook-form';
import { Temporal } from 'temporal-polyfill';
import { z } from 'zod';
import { OriginalTeacherIssueTag } from './-components/original-teacher-issue-tag';
import { OverlappingSessionsModal } from './-components/overlapping-sessions-modal';
import { RefreshZoomLinkButton } from './-components/refresh-zoom-link-button';
import { SessionCancelButton } from './-components/session-cancel-button';
import { SessionCompleteButton } from './-components/session-complete-button';
import { SessionCompletionStatusCell } from './-components/session-completion-status-cell';
import { SessionDeleteButton } from './-components/session-delete-button';
import { SessionStartButton } from './-components/session-start-button';
import { UpdateStudentsButton } from './-components/update-session-students-modal';
import { OriginalTeacherDisplay } from './-components/original-teacher-display';
import {
  getOriginalTeacherIssueDropdownText,
  getOriginalTeacherIssueOptionClassName,
  getOriginalTeacherIssueOptionHint,
  isSubRequestIssue,
  mapIssueFromSessionRemarks,
  orderedOriginalTeacherIssueOptions,
  shouldShowReasonField,
} from '@/lib/original-teacher-issue-utils';
import { validateRouteRole } from '@/lib/routing';
import { FormTextArea } from '@/components/forms/form-text-area';
import { Textarea } from '@/components/ui/textarea';
import { CoachesTypeaheadSearchOptions } from '@/lib/coach-utils';

export const Route = createFileRoute('/_layout/sessions/$sessionId')({
  beforeLoad: validateRouteRole(isAirAdmin),
  component: SessionPage,
});

const editMeetingInfoFormSchema = z.object({
  date: z.instanceof(Temporal.PlainDate),
  overrideTimeZone: timezoneZodSchema.optional(),
  startTime: z.string(),
  endTime: z.string(),
  status: sessionStatusSchema,
  lesson: multiSelectOptions,
  zoom: z
    .object({
      hostUrl: z.string().nullable().optional(),
      attendeeUrl: z.string().nullable().optional(),
      zoomUuid: z.string().nullable().optional(),
    })
    .optional(),
  googleMeet: z
    .object({
      hostUrl: z.string().nullable().optional(),
      attendeeUrl: z.string().nullable().optional(),
    })
    .optional(),
  videoConferenceType: videoConferenceTypeSchema,
});
type EditMeetingInfoForm = z.infer<typeof editMeetingInfoFormSchema>;

const editTeacherInfoFormSchema = z.object({
  originalCoach: requiredSelectOption(),
  substituteCoach: requiredSelectOption(),
  shouldSendConfirmationEmail: z.boolean().default(false).optional(),
  shouldDismissLateTag: z.boolean().default(false).optional(),
  shouldDismissEndEarlyTag: z.boolean().default(false).optional(),
  coachSessionRemarks: multiSelectOptions.optional(),
  coachSessionRemarksV2: multiSelectOptions,
  originalTeacherIssue: originalTeacherIssueSelectableSchema,
  reason: z.string().optional(),
  learningLoss: learningLossSelectableSchema,
  tnsNotes: z.string().nullable(),
});
type EditTeacherInfoForm = z.infer<typeof editTeacherInfoFormSchema>;

function SyncOriginalTeacherIssueFromSessionRemarks({ form }: { form: UseFormReturn<EditTeacherInfoForm> }) {
  const coachSessionRemarksV2 = useWatch({
    control: form.control,
    name: 'coachSessionRemarksV2',
  });
  const originalTeacherIssue = useWatch({
    control: form.control,
    name: 'originalTeacherIssue',
  });

  const mappedIssue = useMemo(
    () => mapIssueFromSessionRemarks(coachSessionRemarksV2.map((option) => option.value as CoachSessionRemarkV2)),
    [coachSessionRemarksV2],
  );
  const hadTriggerRef = useRef(mappedIssue !== 'null');

  useEffect(() => {
    if (mappedIssue !== 'null') {
      // Auto-populate only once when the field is empty.
      if (originalTeacherIssue !== 'null') {
        hadTriggerRef.current = true;
        return;
      }

      form.setValue('originalTeacherIssue', mappedIssue, {
        shouldDirty: true,
        shouldValidate: true,
      });
      hadTriggerRef.current = true;
      return;
    }

    const hadTriggerBefore = hadTriggerRef.current;
    hadTriggerRef.current = false;

    // Only clear after a real trigger removal transition.
    if (!hadTriggerBefore) {
      return;
    }

    // Clear any sub-request issue once sub-class triggers are removed from remarks.
    if (!isSubRequestIssue(originalTeacherIssue)) {
      return;
    }

    form.setValue('originalTeacherIssue', 'null', {
      shouldDirty: true,
      shouldValidate: true,
    });
  }, [form, mappedIssue, originalTeacherIssue]);

  return null;
}

function SessionPage() {
  return (
    <InfoPageSuspenseBoundary
      onError={(e) => {
        const axiosError = axiosErrorFrom(e);
        const airServerError = airServerErrorFrom(e);

        return {
          allowRetry: axiosError?.response?.status !== 404,
          errorHeader: airServerError?.userFacingMessage || 'Failed to load session. Please try again.',
          back: {
            backLink: {
              to: '/sessions',
            },
          },
        };
      }}
    >
      <SessionDetail />
    </InfoPageSuspenseBoundary>
  );
}

function SessionDetail() {
  const { actingUser } = useSession();
  const { sessionId } = Route.useParams();
  const search = Route.useSearch();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const from = (search as { from?: string }).from;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogCoachSession, setDialogCoachSession] = useState<CoachSession | null>(null);
  const [dialogSubmitProps, setDialogSubmitProps] = useState<SubmitProps<EditTeacherInfoForm> | null>(null);

  const getBackLink = () => {
    switch (from) {
      case 'past':
        return '/sessions/past';
      case 'future':
        return '/sessions/future';
      default:
        return '/sessions';
    }
  };
  const [{ data: session, error }] = useSuspenseQueries({
    queries: [getAdminSession(sessionId)],
  });

  const { data: studentGroup } = useSuspenseQuery(getStudentGroup(session.studentGroupId));
  const { data: sessionFeedbacksResp, isPending: isLoadingSessionFeedbacks } = useQuery(
    getSessionFeedbacks({
      filters: {
        sessionId,
      },
      ...allPagesModifier,
    }),
  );
  const orgTimeZone = studentGroup.classTime.schedule.school.org.timeZone;
  const displayTimeZone = FrequentTimeZones.UsCentral;
  const { startInstant, endInstant, coach: originalCoach, substituteCoach } = session;
  const zoomRecordingUrls = session.zoom?.recordingUrls ?? [];

  const { substituteTime, substituteDateTime, coachIdWhoIsTeaching, coachIdToCreateNewSubRequest, subRequestToCancel } =
    useMemo(() => {
      const { mostRecentRequest, coachIdWhoIsTeaching } = getSessionSubstitutionRequestSummary(session);
      const hasPendingSubRequest = mostRecentRequest?.status === 'pending';
      const adminCanOpenNewSubRequest =
        (session.status === SessionStatus.scheduled || session.status === SessionStatus.in_progress) &&
        !hasPendingSubRequest &&
        // can only request sub before session end time
        InstantCompare(Temporal.Now.instant()).isBeforeOrEqual(session.endInstant) &&
        !!coachIdWhoIsTeaching;
      const subRequestToCancel = mostRecentRequest?.status === 'pending' ? mostRecentRequest : undefined;
      const coachIdToCreateNewSubRequest = adminCanOpenNewSubRequest ? coachIdWhoIsTeaching : undefined;
      const { formattedTime: substituteTime, formattedDateTime: substituteDateTime } =
        mostRecentRequest?.createdAtInstant
          ? formatInstant(mostRecentRequest.createdAtInstant, displayTimeZone, undefined, ', ')
          : {};

      return {
        substituteTime,
        substituteDateTime,
        coachIdWhoIsTeaching,
        coachIdToCreateNewSubRequest,
        subRequestToCancel,
      };
    }, [session, displayTimeZone]);

  const sessionFeedbacks = useMemo(
    () =>
      // API should've deleted feedbacks from non-teaching coaches. But to be safe, we filter them out here
      (sessionFeedbacksResp?.sessionFeedbacks || EmptyArray).filter(({ coachId }) => coachId === coachIdWhoIsTeaching),
    [coachIdWhoIsTeaching, sessionFeedbacksResp?.sessionFeedbacks],
  );

  const { mutateAsync: updateSessionDetailsMutate } = useMutation({
    mutationFn: updateSessionDetails,
  });

  const { mutateAsync: updateSessionPeopleMutate } = useMutation({
    mutationFn: updateSessionPeople,
  });

  const didCatchError = useCallback(
    (title: string, e: unknown) => {
      const error = airServerErrorFrom(e);
      if (error) {
        toast({
          title,
          description: error.userFacingMessage,
          variant: 'destructive',
        });
      }
    },
    [toast],
  );

  useEffect(() => {
    if (!dialogOpen) {
      setDialogCoachSession(null);
      setDialogSubmitProps(null);
    }
  }, [dialogOpen]);

  const onSubmitMeetingInfo = ({ formData, disableEditing }: SubmitProps<EditMeetingInfoForm>) => {
    const { date, startTime, endTime, overrideTimeZone, lesson, videoConferenceType: vcType, ...rest } = formData;

    const startPlainTime = Temporal.PlainTime.from(startTime);
    const endPlainTime = Temporal.PlainTime.from(endTime);
    if (Temporal.PlainTime.compare(startPlainTime, endPlainTime) >= 0) {
      toast({
        title: 'Invalid time',
        description: 'Please enter a valid time range. The end time must be after the start time.',
      });
      return;
    }

    const sessionTimeZone = overrideTimeZone?.value || displayTimeZone;
    if (!sessionTimeZone) {
      toast({
        title: 'Invalid time zone',
        description: `The session's district does not have a time zone and you did not provide an override time zone. Either update the district to have a time zone or provide an override time zone.`,
      });
      return;
    }

    const updatedLesson = lesson.at(0);
    if (!updatedLesson || !updatedLesson.value) {
      toast({
        title: 'Invalid lesson',
        description: 'Please select a valid lesson for the session.',
      });
      return;
    }

    const start = date.toZonedDateTime({ plainTime: startPlainTime, timeZone: sessionTimeZone });
    const end = date.toZonedDateTime({ plainTime: endPlainTime, timeZone: sessionTimeZone });

    const updateData: UpdateSessionOptions = {
      ...rest,
      videoConferenceType: vcType,
      start: start.toInstant().toString(),
      end: end.toInstant().toString(),
      lessonId: updatedLesson.value,
      googleMeet: {
        hostUrl: formData.googleMeet?.hostUrl,
        attendeeUrl: formData.googleMeet?.hostUrl, // Overwrite attendee URL with host URL
      },
    };
    return updateSessionDetailsMutate({
      id: sessionId,
      data: updateData,
    })
      .then(() => {
        toast({ title: 'Session meeting information updated successfully' });
        disableEditing();
        queryClient.invalidateQueries(getAdminSession(sessionId));
        queryClient.invalidateQueries(getAdminSessions());
        queryClient.invalidateQueries(getSessionsForStudentGroup(session.studentGroupId));
        queryClient.invalidateQueries(getSessionFeedbacks());
      })
      .catch((e) => didCatchError('Session meeting information update failed', e));
  };

  const onSubmitTeacherInfo = async (submitProps: SubmitProps<EditTeacherInfoForm>) => {
    const { formData, disableEditing } = submitProps;
    const {
      originalCoach,
      substituteCoach,
      shouldSendConfirmationEmail,
      originalTeacherIssue,
      reason,
      coachSessionRemarks,
      coachSessionRemarksV2,
      learningLoss,
      tnsNotes,
      ...others
    } = formData;

    if (!originalCoach.value && shouldSendConfirmationEmail) {
      toast({
        title: 'Invalid original teacher',
        description: 'Please select a valid original teacher for the session.',
      });
      return;
    }

    // Check if substitute coach has conflicts before updating
    const { coachHasConflicts, coachSessions } = await getSubstituteCoachConflictsWithSession({
      originalCoachId: originalCoach.value,
      substituteCoachId: substituteCoach.value,
      session,
      queryClient,
    });

    if (coachHasConflicts && coachSessions && coachSessions.length > 0 && !dialogCoachSession && !dialogSubmitProps) {
      setDialogCoachSession(coachSessions[0]);
      setDialogSubmitProps(submitProps);
      setDialogOpen(true);
      return;
    }

    return updateSessionPeopleMutate({
      sessionId,
      payload: {
        ...others,
        studentIds: session.students.map((student) => student.id),
        coachId: originalCoach.value,
        substituteCoachId: substituteCoach.value,
        shouldSendConfirmationEmail,
        shouldSendUpdateConfirmationEmail: true,
        originalTeacherIssue: nullIfString(originalTeacherIssue),
        originalTeacherIssueReason: shouldShowReasonField(originalTeacherIssue)
          ? normalizeOriginalTeacherIssueReason(reason)
          : null,
        coachSessionRemarks: coachSessionRemarks?.map((option) => option.value as CoachSessionRemark) || [],
        coachSessionRemarksV2: coachSessionRemarksV2.map((option) => option.value as CoachSessionRemarkV2),
        learningLoss: nullIfString(learningLoss),
        tnsNotes,
      },
    })
      .then(({ coachConflict, substituteCoachConflict }) => {
        toast({ title: 'Session teacher information updated successfully' });
        if (coachConflict) {
          const coachName = formData.originalCoach?.label ?? formData.originalCoach?.value;
          toast({
            title: `Teacher ${coachName} has another conflicting session. Please double check the session details.`,
          });
        }
        if (substituteCoachConflict) {
          const substituteCoachName = formData.substituteCoach?.label ?? formData.substituteCoach?.value;
          toast({
            title: `Substitute teacher ${substituteCoachName} has another conflicting session. Please double check the session details.`,
          });
        }
        disableEditing();
        queryClient.invalidateQueries(getAdminSession(sessionId));
        queryClient.invalidateQueries(getAdminSessions());
        queryClient.invalidateQueries(getSessionsForStudentGroup(session.studentGroupId));
        queryClient.invalidateQueries(getSessionFeedbacks());
        setDialogOpen(false);
      })
      .catch((e) => didCatchError('Session teacher information update failed', e));
  };

  if (error) {
    toast({ title: 'Error while fetching session' });
    return <div>Failed to fetch session</div>;
  }

  const showStartSessionButton =
    session.status === SessionStatus.scheduled &&
    Temporal.Instant.compare(startInstant, Temporal.Now.instant()) <= 0 &&
    Temporal.Instant.compare(endInstant, Temporal.Now.instant()) >= 0;
  const showEndSessionButton = session.status === SessionStatus.in_progress;

  const coachFilters: CoachesTypeaheadSearchOptions = {
    filters: {
      statuses: [CoachStatus.ready_to_teach],
    },
  };

  return (
    <div className="mb-2">
      <InfoPage
        headerText="Session Details"
        headerActions={
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button className="ml-auto h-8 flex" variant="secondary">
                Actions
                <ChevronDown className="ml-2 h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[200px]">
              {showStartSessionButton && (
                <SessionStartButton
                  session={session}
                  trigger={<DropdownMenuItem onSelect={(e) => e.preventDefault()}>Start Session</DropdownMenuItem>}
                />
              )}
              {showEndSessionButton && (
                <SessionCompleteButton
                  session={session}
                  trigger={<DropdownMenuItem onSelect={(e) => e.preventDefault()}>Complete Session</DropdownMenuItem>}
                />
              )}
              {coachIdToCreateNewSubRequest && (
                <CreateSubstitutionRequestByAdmin
                  session={session}
                  coachIdToCreateSubRequestFor={coachIdToCreateNewSubRequest}
                  trigger={<DropdownMenuItem onSelect={(e) => e.preventDefault()}>Create Sub Request</DropdownMenuItem>}
                />
              )}
              {subRequestToCancel && (
                <CancelSessionSubstituteByAdmin
                  session={session}
                  subRequest={subRequestToCancel}
                  trigger={<DropdownMenuItem onSelect={(e) => e.preventDefault()}>Cancel Sub Request</DropdownMenuItem>}
                />
              )}
              <RefreshZoomLinkButton
                session={session}
                trigger={<DropdownMenuItem onSelect={(e) => e.preventDefault()}>Refresh Zoom Link</DropdownMenuItem>}
              />
              <UpdateStudentsButton
                session={session}
                trigger={<DropdownMenuItem onSelect={(e) => e.preventDefault()}>Update Students</DropdownMenuItem>}
              />
              <SessionCancelButton
                session={session}
                trigger={<DropdownMenuItem onSelect={(e) => e.preventDefault()}>Cancel Session</DropdownMenuItem>}
              />
              <SessionDeleteButton
                session={session}
                from={from}
                schoolId={session?.school?.id}
                trigger={<DropdownMenuItem onSelect={(e) => e.preventDefault()}>Delete Session</DropdownMenuItem>}
              />
            </DropdownMenuContent>
          </DropdownMenu>
        }
        breadcrumbs={{
          linkItems: [
            {
              label: 'Sessions',
              link: {
                to: getBackLink(),
              },
            },
          ],
          currentPage: 'Session Details',
        }}
      >
        <EditableInfoCard
          headerText="Meeting information"
          formSchema={editMeetingInfoFormSchema}
          defaultValues={{
            date: startInstant.toZonedDateTimeISO(displayTimeZone).toPlainDate(),
            startTime: toInputTimeValue(startInstant.toZonedDateTimeISO(displayTimeZone).toPlainTime()),
            endTime: toInputTimeValue(endInstant.toZonedDateTimeISO(displayTimeZone).toPlainTime()),
            status: session.status,
            lesson: [{ value: session.lesson.id, label: session.lesson.name }],
            zoom: {
              hostUrl: session.zoom?.hostUrl,
              attendeeUrl: session.zoom?.attendeeUrl,
              zoomUuid: session.zoom?.zoomUuid,
            },
            googleMeet: {
              hostUrl: session.googleMeet?.hostUrl,
              attendeeUrl: session.googleMeet?.attendeeUrl,
            },
            videoConferenceType: session.videoConferenceType,
          }}
          readonly={!isAirAdmin(actingUser)}
          onSubmit={onSubmitMeetingInfo}
        >
          {({ editing, form }) => {
            const selectionTimeZone = form.watch('overrideTimeZone') || displayTimeZone;
            const videoConferenceType = editing ? form.watch('videoConferenceType') : session.videoConferenceType;
            return (
              <>
                <InfoKeyValue
                  label="Date"
                  value={formatInstant(startInstant, displayTimeZone).formattedDate}
                  edit={{
                    editing,
                    editModeComponent: <FormInputDate name="date" label="Date" form={form} />,
                  }}
                />
                <InfoKeyValue
                  label="Scheduled time"
                  value={`${getSessionFormattedSchedule({ startInstant, endInstant, displayTimeZone }).sessionTimeFormatted} (${displayTimeZone})`}
                  edit={{
                    editing,
                    editModeComponent: (
                      <FormComboBox
                        name="overrideTimeZone"
                        label={
                          <div className="flex items-center gap-2">
                            <p className="font-bold">Schedule override time zone</p>
                            <Tooltip trigger={<Info className="h-4 w-4" />}>
                              The date/time you selected uses the student group's district's time zone ({orgTimeZone})
                              by default. You can override this if needed.
                            </Tooltip>
                          </div>
                        }
                        form={form}
                        triggerProps={{ className: 'w-[80%]' }}
                        {...comboBoxOptionsOf(AllTimeZones)}
                      />
                    ),
                  }}
                />
                <InfoKeyValue
                  label="Meeting status"
                  value={
                    <div className="flex items-center gap-2">
                      {session.meeting?.status ? (
                        <div className="flex flex-col gap-2">
                          {MeetingStatusLabelMap[session.meeting.status]}
                          {session.meeting.status === MeetingStatus.canceled && session.canceledAtInstant && (
                            <span className="text-xs text-gray-500">
                              Canceled at{' '}
                              {
                                formatInstant(session.canceledAtInstant, displayTimeZone, undefined, ', ')
                                  .formattedDateTime
                              }
                            </span>
                          )}
                        </div>
                      ) : (
                        SessionStatusToLabel[session.status]
                      )}
                    </div>
                  }
                />
                <InfoKeyValue
                  label="Recording links"
                  value={
                    zoomRecordingUrls.length > 0 ? (
                      <div className="flex flex-col gap-1">
                        {zoomRecordingUrls.map((url, index) => (
                          <LinkToExternal
                            key={`${url}-${index}`}
                            href={url}
                            className="text-blue-600 hover:text-blue-800 underline"
                          >
                            {zoomRecordingUrls.length > 1 ? `Recording ${index + 1}` : 'View recording'}
                          </LinkToExternal>
                        ))}
                      </div>
                    ) : (
                      '-'
                    )
                  }
                />
                {editing ? (
                  <>
                    <FormInputField
                      form={form}
                      name="startTime"
                      label={`Schedule start time (${selectionTimeZone})`}
                      inputProps={{ type: 'time' }}
                    />
                    <FormInputField
                      form={form}
                      name="endTime"
                      label={`Schedule end time (${selectionTimeZone})`}
                      inputProps={{ type: 'time' }}
                    />
                  </>
                ) : (
                  <>
                    <InfoKeyValue
                      label="Actual start time"
                      value={
                        session.meeting?.startInstant
                          ? formatInstant(session.meeting.startInstant, displayTimeZone).formattedTime
                          : '-'
                      }
                    />
                    <InfoKeyValue
                      label="Actual end time"
                      value={
                        session.meeting?.endInstant
                          ? formatInstant(session.meeting.endInstant, displayTimeZone).formattedTime
                          : '-'
                      }
                    />
                  </>
                )}
                <InfoKeyValue
                  label="Lesson"
                  value={
                    <CopyableLink to="/lessons/$lessonId" params={{ lessonId: session.lesson.id }}>
                      {session.lesson.name}
                    </CopyableLink>
                  }
                  edit={{
                    editing,
                    editModeComponent: (
                      <FormMultiSelect
                        form={form}
                        label="Lesson"
                        name="lesson"
                        multipleSelectorProps={{
                          maxSelected: 1,
                          onSearch: lessonTypeaheadSearch(queryClient),
                          hideClearAllButton: true,
                        }}
                        className="w-[80%]"
                      />
                    ),
                  }}
                />
                <InfoKeyValue
                  label="Meeting type"
                  value={session.videoConferenceType ? VideoConferenceTypeLabels[session.videoConferenceType] : '-'}
                  edit={{
                    editing,
                    editModeComponent: (
                      <FormSingleSelect
                        form={form}
                        name="videoConferenceType"
                        label="Meeting type"
                        options={videoConferenceTypeSchema.options}
                        renderOption={(option) => VideoConferenceTypeLabels[option]}
                        triggerProps={{ className: 'w-[80%]' }}
                      />
                    ),
                  }}
                />
                {videoConferenceType === VideoConferenceType.zoom && (
                  <>
                    <InfoKeyValue
                      label="Zoom link"
                      value={
                        <CopyableLink
                          externalHref={session.zoom?.attendeeUrl ?? ''}
                          whenUrlEmptyRender={
                            !session.coachId
                              ? 'No teacher assigned to this session. Attendee URL will be auto-populated when a teacher is assigned'
                              : 'Not set yet. You can set it manually'
                          }
                        >
                          {session.zoom?.attendeeUrl ?? 'Join class'}
                        </CopyableLink>
                      }
                      edit={{
                        editing,
                        editModeComponent: (
                          <FormInputField
                            form={form}
                            name="zoom.attendeeUrl"
                            label="Zoom link"
                            inputProps={{ type: 'url' }}
                          />
                        ),
                      }}
                    />
                    <InfoKeyValue label="Zoom ID" value={session.zoom?.zoomUuid ?? '-'} />
                    <InfoKeyValue
                      label="Zoom host URL"
                      value={
                        <CopyableLink
                          externalHref={session.zoom?.hostUrl ?? ''}
                          whenUrlEmptyRender="Host URL not set yet. This will be auto-populated 2hrs before session starts, or you can set it manually"
                        >
                          Start class
                        </CopyableLink>
                      }
                      edit={{
                        editing,
                        editModeComponent: (
                          <FormInputField
                            form={form}
                            name="zoom.hostUrl"
                            label="Zoom host URL"
                            inputProps={{ type: 'url' }}
                          />
                        ),
                      }}
                    />
                  </>
                )}
                {videoConferenceType === VideoConferenceType.googleMeet && (
                  <InfoKeyValue
                    label="Google Meet URL"
                    value={
                      <CopyableLink
                        externalHref={session.googleMeet?.hostUrl ?? session.googleMeet?.attendeeUrl ?? ''}
                        whenUrlEmptyRender={
                          !session.coachId
                            ? 'No teacher assigned to this session. Attendee URL will be auto-populated when a teacher is assigned'
                            : 'Not set yet. You can set it manually'
                        }
                      >
                        {session.googleMeet?.hostUrl ?? session.googleMeet?.attendeeUrl ?? 'Join class'}
                      </CopyableLink>
                    }
                    edit={{
                      editing,
                      editModeComponent: (
                        <FormInputField
                          form={form}
                          name="googleMeet.hostUrl"
                          label="Google Meet URL"
                          inputProps={{ type: 'url' }}
                        />
                      ),
                    }}
                  />
                )}
              </>
            );
          }}
        </EditableInfoCard>
        <EditableInfoCard
          key={`teacher-${session.id}-${JSON.stringify(session.coachSessionRemarksV2)}`}
          headerText="Teacher"
          formSchema={editTeacherInfoFormSchema}
          defaultValues={{
            originalCoach: originalCoach ? buildUserSelectOptions(originalCoach) : emptySelectOption,
            substituteCoach: substituteCoach
              ? buildUserSelectOptions(substituteCoach)
              : originalCoach
                ? buildUserSelectOptions(originalCoach)
                : emptySelectOption,
            shouldSendConfirmationEmail:
              !!originalCoach && !!substituteCoach && originalCoach.id !== substituteCoach.id,
            shouldDismissLateTag: !!session.shouldDismissLateTag,
            shouldDismissEndEarlyTag: !!session.shouldDismissEndEarlyTag,
            coachSessionRemarks:
              session.coachSessionRemarks.map((tag) => ({ value: tag, label: getCoachSessionRemarkLabel(tag) })) || [],
            coachSessionRemarksV2: buildSessionRemarksV2Options({ options: session.coachSessionRemarksV2 }),
            originalTeacherIssue: stringIfNull(session.originalTeacherIssue),
            reason: session.originalTeacherIssueReason ?? '',
            learningLoss: stringIfNull(session.learningLoss),
            tnsNotes: session.tnsNotes,
          }}
          readonly={!isAirAdmin(actingUser)}
          onSubmit={onSubmitTeacherInfo}
        >
          {({ editing, form }) => {
            const isSubstituteCoach = form.watch('originalCoach')?.value !== form.watch('substituteCoach')?.value;
            const coachSessionRemarkV2 = form
              .watch('coachSessionRemarksV2')
              .map((t) => t.value as CoachSessionRemarkV2);
            const watchedOriginalTeacherIssue = form.watch('originalTeacherIssue');
            const showReasonField = shouldShowReasonField(watchedOriginalTeacherIssue);

            return (
              <>
                {editing && <SyncOriginalTeacherIssueFromSessionRemarks form={form} />}
                <InfoKeyValue
                  label="Session teacher"
                  value={<SessionTeacherDisplay session={session} />}
                  edit={{
                    editing,
                    editModeComponent: (
                      <FormComboBox
                        form={form}
                        label="Session teacher"
                        name="substituteCoach"
                        placeholder="Select session teacher"
                        {...comboBoxOptionsOf(coachesTypeaheadSearch(queryClient, coachFilters))}
                        triggerProps={{ className: 'w-2/3' }}
                        triggerSearchOnFocus
                      />
                    ),
                  }}
                />
                <InfoKeyValue
                  label="Original teacher"
                  value={<OriginalTeacherDisplay session={session} />}
                  edit={{
                    editing,
                    editModeComponent: (
                      <FormComboBox
                        form={form}
                        label="Original teacher"
                        name="originalCoach"
                        placeholder="Select original teacher"
                        {...comboBoxOptionsOf(coachesTypeaheadSearch(queryClient, coachFilters))}
                        triggerProps={{ className: 'w-2/3' }}
                        triggerSearchOnFocus
                      />
                    ),
                  }}
                />
                <InfoKeyValue
                  label="Substitute time"
                  value={
                    substituteTime ? (
                      <span className="text-xs text-gray-500">
                        Sub requested at {substituteDateTime} ({displayTimeZone})
                      </span>
                    ) : (
                      '-'
                    )
                  }
                  edit={{
                    editing,
                    editModeComponent: isSubstituteCoach ? (
                      <FormCheckbox name="shouldSendConfirmationEmail" form={form} label="Send confirmation email?" />
                    ) : (
                      <div className="space-y-2 flex flex-col gap-2 mt-1">
                        <FormLabel className="font-bold">Substitute time</FormLabel>-
                      </div>
                    ),
                  }}
                />
                <InfoKeyValue
                  label="Original teacher issue"
                  value={
                    session.originalTeacherIssue ? (
                      <div className="space-y-1">
                        <OriginalTeacherIssueTag issue={session.originalTeacherIssue} />
                        {shouldShowReasonField(session.originalTeacherIssue) &&
                        session.originalTeacherIssueReason?.trim() ? (
                          <div className="pr-1 text-sm">
                            <span className="font-medium">Reason:</span>{' '}
                            <span className="whitespace-pre-wrap break-words line-clamp-3">
                              {session.originalTeacherIssueReason}
                            </span>
                          </div>
                        ) : null}
                      </div>
                    ) : (
                      '-'
                    )
                  }
                  edit={{
                    editing,
                    editModeComponent: (
                      <div className="space-y-2">
                        <FormSingleSelect
                          key={`originalTeacherIssue-${watchedOriginalTeacherIssue}`}
                          name="originalTeacherIssue"
                          label="Original teacher issue"
                          form={form}
                          options={orderedOriginalTeacherIssueOptions}
                          renderOption={(issue) => {
                            const optionHint = getOriginalTeacherIssueOptionHint(issue);
                            return (
                              <div>
                                <div>{getOriginalTeacherIssueDropdownText(issue)}</div>
                                {optionHint ? <div className="text-muted-foreground">{optionHint}</div> : null}
                              </div>
                            );
                          }}
                          renderOptionSelected={getOriginalTeacherIssueDropdownText}
                          optionClassName={getOriginalTeacherIssueOptionClassName}
                          triggerProps={{ className: 'w-2/3 [&>span]:block [&>span]:text-left' }}
                        />
                        {showReasonField ? (
                          <FormTextArea
                            name="reason"
                            label="Reason"
                            form={form}
                            textAreaProps={{
                              className: 'w-2/3 resize-none max-h-24',
                              placeholder: 'Please enter a reason',
                              rows: 3,
                            }}
                          />
                        ) : null}
                      </div>
                    ),
                  }}
                />
                <InfoKeyValue
                  label={
                    <div className="flex items-center gap-2">
                      <span>Session remarks</span>
                    </div>
                  }
                  value={
                    <div className="flex flex-wrap gap-1">
                      {session.coachSessionRemarksV2.map((tag) => (
                        <Badge key={tag} variant="outline" className="text-center">
                          {formatCoachSessionRemarkV2Label(tag, session)}
                        </Badge>
                      ))}
                      {session.coachSessionRemarksV2.length === 0 && '-'}
                    </div>
                  }
                  edit={{
                    editing,
                    editModeComponent: (
                      <FormMultiSelect
                        form={form}
                        name="coachSessionRemarksV2"
                        label="Session remark"
                        multipleSelectorProps={{
                          options: buildSessionRemarksV2Options({ selected: coachSessionRemarkV2 }),
                          className: 'w-2/3',
                        }}
                      />
                    ),
                  }}
                />
                <InfoKeyValue
                  label="Teacher display - Class status"
                  value={
                    session.sessionCompletionStatus ? (
                      <SessionCompletionStatusCell
                        status={session.sessionCompletionStatus}
                        coachSessionRemarksV2={session.coachSessionRemarksV2}
                        session={session}
                      />
                    ) : (
                      '-'
                    )
                  }
                  edit={{
                    editing,
                    editModeComponent: (
                      <div className="space-y-2 flex flex-col gap-2 mt-1">
                        <FormLabel className="font-bold">Teacher display - Class status</FormLabel>
                        <div className="w-2/3">
                          <SessionCompletionStatusCell
                            status={session.sessionCompletionStatus}
                            coachSessionRemarksV2={session.coachSessionRemarksV2}
                            session={session}
                          />
                        </div>
                      </div>
                    ),
                  }}
                />

                <InfoKeyValue
                  label="Learning loss"
                  value={session.learningLoss ? LearningLossLabelMap[session.learningLoss] : '-'}
                  edit={{
                    editing,
                    editModeComponent: (
                      <FormSingleSelect
                        name="learningLoss"
                        label="Learning loss"
                        form={form}
                        options={learningLossSelectableValues}
                        renderOption={getLearningLossLabel}
                        triggerProps={{ className: 'w-2/3' }}
                      />
                    ),
                  }}
                />
                <InfoKeyValue
                  label="TNS notes"
                  value={
                    session.tnsNotes?.trim() ? (
                      <Textarea value={session.tnsNotes} className="w-2/3 resize-none" readOnly />
                    ) : (
                      '-'
                    )
                  }
                  edit={{
                    editing,
                    editModeComponent: (
                      <FormTextArea
                        name="tnsNotes"
                        label="TNS notes"
                        form={form}
                        textAreaProps={{ className: 'w-2/3 resize-none' }}
                      />
                    ),
                  }}
                />
              </>
            );
          }}
        </EditableInfoCard>
        <InfoCard headerText="Students">
          <SessionStudentsTable session={session} feedbacks={sessionFeedbacks} isLoading={isLoadingSessionFeedbacks} />
        </InfoCard>
      </InfoPage>
      {dialogCoachSession && dialogSubmitProps && (
        <OverlappingSessionsModal
          coachSession={dialogCoachSession}
          open={dialogOpen}
          setOpen={setDialogOpen}
          onConfirm={() => onSubmitTeacherInfo(dialogSubmitProps)}
        />
      )}
    </div>
  );
}
